import { useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  Calendar,
  Flame,
  PieChart,
  FileDown,
  Users,
  Layers,
  Wrench,
  Grid3X3,
} from 'lucide-react';
import type { VcfWorkstream } from '@/hooks/useWorkstreams';
import { useExportProjectPptx } from '@/hooks/useProjects';
import type { RateCardEntryWithValues } from '@/hooks/useRateCards';

interface ProjectSummaryTabProps {
  projectId: string;
  projectName: string;
  workstreams: VcfWorkstream[];
  selectedCapabilities: Set<string>;
  guardrailValues: Record<string, string>;
  currency?: 'GBP' | 'USD' | 'EUR';
  architectOversightPct?: number;
  pmPct?: number;
  bcArchitectOversightPct?: number;
  bcPmPct?: number;
  daysPerWeek?: number;
  entitlementValue?: number;
  totalCost: number;
  rateCardEntries?: RateCardEntryWithValues[];
}

export function ProjectSummaryTab({
  projectId,
  projectName,
  workstreams,
  selectedCapabilities,
  guardrailValues,
  currency = 'USD',
  architectOversightPct = 1000,
  pmPct = 2000,
  bcArchitectOversightPct = 500,
  bcPmPct = 500,
  daysPerWeek = 5,
  entitlementValue = 0,
  rateCardEntries = [],
}: ProjectSummaryTabProps) {
  const exportPptx = useExportProjectPptx();

  // Build rate map from actual rate card entries (role name → daily rate in cents)
  // The API returns xtravirtDailyGBP (uppercase B) — handle both casings for safety
  const rateMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of rateCardEntries) {
      if (!entry.role) continue;
      const rate = (entry as unknown as Record<string, unknown>).xtravirtDailyGBP as number
        || entry.xtravirtDailyGbp
        || 0;
      if (rate > 0) {
        map.set(entry.role, rate);
      }
    }
    return map;
  }, [rateCardEntries]);

  // Helper: get rate for a role from the rate card, with no fallback (0 if missing)
  const getRate = (role: string): number => rateMap.get(role) || 0;

  // BC percentages (basis points to percentage)
  const bcArchitectPct = (bcArchitectOversightPct || 500) / 100;
  const bcPmPctVal = (bcPmPct || 500) / 100;

  const summary = useMemo(() => {
    let totalBaseDays = 0;
    const byRole: Record<string, { days: number; hours: number; cost: number }> = {};
    const bySkill: Record<string, { days: number; hours: number }> = {};
    const byWorkstream: Record<
      string,
      {
        name: string;
        days: number;
        hours: number;
        cost: number;
        startWeek: number;
        endWeek: number;
        allocationPct: number;
        byRole: Record<string, number>;
      }
    > = {};

    let currentStartWeek = 1;

    for (const ws of workstreams) {
      let wsDays = 0;
      let wsCost = 0;
      const wsStartWeek = currentStartWeek;
      const wsByRole: Record<string, number> = {};

      for (const cap of ws.capabilities || []) {
        if (!selectedCapabilities.has(cap.id)) continue;

        // Calculate guardrail multiplier for this capability using structured fields
        let guardrailFactor = 1;
        for (const gr of cap.guardrails || []) {
          const inputVal = guardrailValues[gr.id];
          if (!inputVal) continue;
          const mf = gr.multiplierFactor;
          if (mf == null || mf === 0) continue;
          const step = gr.multiplierStep || 1;
          const bv = gr.baselineValue ?? 0;

          switch (gr.inputType) {
            case 'number': {
              const current = parseFloat(inputVal);
              if (!isNaN(current)) {
                guardrailFactor += mf * Math.max(0, Math.floor((current - bv) / step));
              }
              break;
            }
            case 'boolean':
              if (inputVal === 'true') guardrailFactor += mf;
              break;
            case 'select':
              if (gr.optionFactors) {
                guardrailFactor += gr.optionFactors[inputVal] ?? 0;
              }
              break;
          }
        }

        // Sum activities
        for (const act of cap.activities || []) {
          const baseDays = (act.baseDays || 0) / 100;
          const adjustedDays = baseDays * guardrailFactor;
          const rate = getRate(act.role);
          const cost = adjustedDays * rate;

          totalBaseDays += baseDays;
          wsDays += adjustedDays;
          wsCost += cost;

          if (!byRole[act.role]) {
            byRole[act.role] = { days: 0, hours: 0, cost: 0 };
          }
          byRole[act.role].days += adjustedDays;
          byRole[act.role].hours += adjustedDays * 8;
          byRole[act.role].cost += cost;

          // Track by skill
          const skillName = act.skill || 'General';
          if (!bySkill[skillName]) {
            bySkill[skillName] = { days: 0, hours: 0 };
          }
          bySkill[skillName].days += adjustedDays;
          bySkill[skillName].hours += adjustedDays * 8;

          // Track by workstream-role
          wsByRole[act.role] = (wsByRole[act.role] || 0) + adjustedDays;
        }
      }

      if (wsDays > 0) {
        const wsWeeks = Math.ceil(wsDays / daysPerWeek);
        byWorkstream[ws.id] = {
          name: ws.name,
          days: wsDays,
          hours: wsDays * 8,
          cost: wsCost,
          startWeek: wsStartWeek,
          endWeek: wsStartWeek + wsWeeks - 1,
          allocationPct: 0, // Will be calculated
          byRole: wsByRole,
        };
        currentStartWeek += Math.max(1, Math.ceil(wsWeeks * 0.5)); // Overlap by 50%
      }
    }

    // Calculate totals
    const totalResourceDays = Object.values(byRole).reduce((sum, r) => sum + r.days, 0);
    const oversightDays = totalResourceDays * (architectOversightPct / 10000);
    const pmDays = totalResourceDays * (pmPct / 10000);
    const bcOversightDays = totalResourceDays * (bcArchitectOversightPct / 10000);
    const bcPmDays = totalResourceDays * (bcPmPct / 10000);
    const totalDays = totalResourceDays + oversightDays + pmDays + bcOversightDays + bcPmDays;
    const totalHours = totalDays * 8;
    const durationWeeks = Math.ceil(totalDays / daysPerWeek);

    // Calculate allocation percentages
    const totalWsDays = Object.values(byWorkstream).reduce((sum, ws) => sum + ws.days, 0);
    for (const ws of Object.values(byWorkstream)) {
      ws.allocationPct = totalWsDays > 0 ? (ws.days / totalWsDays) * 100 : 0;
    }

    // Resource costs (XV overhead included in resource costs line)
    const resourceCost = Object.values(byRole).reduce((sum, r) => sum + r.cost, 0);
    const oversightCost = oversightDays * getRate('Senior Architect');
    const pmCost = pmDays * getRate('Project Manager');

    // BC costs (uses Offshore Architect / Offshore Project Manager rates)
    const bcOversightCost = bcOversightDays * getRate('Offshore Architect');
    const bcPmCost = bcPmDays * getRate('Offshore Project Manager');

    const totalProjectCost = resourceCost + oversightCost + pmCost + bcOversightCost + bcPmCost;

    // Collect all unique roles across workstreams for the pivot table
    const allRoles = new Set<string>();
    for (const ws of Object.values(byWorkstream)) {
      for (const role of Object.keys(ws.byRole)) {
        allRoles.add(role);
      }
    }

    return {
      totalBaseDays,
      totalResourceDays,
      oversightDays,
      pmDays,
      bcOversightDays,
      bcPmDays,
      totalDays,
      totalHours,
      durationWeeks,
      resourceCost,
      oversightCost,
      pmCost,
      bcOversightCost,
      bcPmCost,
      totalProjectCost,
      byRole,
      bySkill,
      byWorkstream,
      allRoles: Array.from(allRoles).sort(),
    };
  }, [workstreams, selectedCapabilities, guardrailValues, architectOversightPct, pmPct, bcArchitectOversightPct, bcPmPct, daysPerWeek, getRate]);

  // Use the client-calculated total (not the denormalized DB field which may be stale)
  const calculatedCost = summary.totalProjectCost;
  const remainingEntitlement = entitlementValue - calculatedCost;
  const remainingPct = entitlementValue > 0 ? (remainingEntitlement / entitlementValue) * 100 : 0;
  const utilizationPct = entitlementValue > 0 ? (calculatedCost / entitlementValue) * 100 : 0;
  const avgDailyBurnRate =
    summary.durationWeeks > 0 ? calculatedCost / (summary.durationWeeks * daysPerWeek) : 0;

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatDays = (days: number) => days.toFixed(1);

  // Total skill days for distribution calc
  const totalSkillDays = Object.values(summary.bySkill).reduce((sum, s) => sum + s.days, 0);

  return (
    <div className="space-y-6">
      {/* Header with Export Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-xtravirt-dark">
            Project Summary
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Overview of project scope, costs, and resource allocation
          </p>
        </div>
        <button
          onClick={() => exportPptx.mutate({ projectId, projectName })}
          disabled={exportPptx.isPending}
          className="btn-secondary flex items-center gap-2"
        >
          <FileDown className="h-4 w-4" />
          {exportPptx.isPending ? 'Exporting...' : 'Export to PowerPoint'}
        </button>
      </div>

      {/* Summary Cards Grid */}
      <div className="summary-card-grid">
        <div className="summary-card">
          <div className="summary-card-label">
            <DollarSign className="inline h-3.5 w-3.5 mr-1" />
            Unscoped Opportunity Value
          </div>
          <div className="summary-card-value accent">
            {formatCurrency(entitlementValue)}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-label">
            <DollarSign className="inline h-3.5 w-3.5 mr-1" />
            Services Cost
          </div>
          <div className="summary-card-value success">
            {formatCurrency(calculatedCost)}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-label">
            <TrendingUp className="inline h-3.5 w-3.5 mr-1" />
            Remaining Services
          </div>
          <div className={`summary-card-value ${remainingEntitlement >= 0 ? 'success' : 'warning'}`}>
            {formatCurrency(remainingEntitlement)}
            {entitlementValue > 0 && (
              <div className="text-xs font-normal text-gray-500 mt-0.5">
                ({remainingPct.toFixed(1)}% remaining)
              </div>
            )}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-label">
            <Calendar className="inline h-3.5 w-3.5 mr-1" />
            Duration
          </div>
          <div className="summary-card-value">
            {summary.durationWeeks} weeks
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-label">
            <Flame className="inline h-3.5 w-3.5 mr-1" />
            Avg Daily Burn Rate
          </div>
          <div className="summary-card-value">
            {formatCurrency(avgDailyBurnRate)}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-label">
            <PieChart className="inline h-3.5 w-3.5 mr-1" />
            Utilization
          </div>
          <div className={`summary-card-value ${utilizationPct <= 100 ? 'success' : 'warning'}`}>
            {utilizationPct.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Services Summary Table */}
      <div className="card overflow-hidden !p-0">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h4 className="text-sm font-semibold text-xtravirt-dark flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-xtravirt-navy" />
            Services Summary
          </h4>
        </div>
        <table className="entitlement-table">
          <tbody>
            <tr>
              <td>Unscoped Opportunity Value</td>
              <td className="text-right font-medium">{formatCurrency(entitlementValue)}</td>
            </tr>
            <tr>
              <td>Less: Resource Costs</td>
              <td className="text-right text-red-600">-{formatCurrency(summary.resourceCost + summary.oversightCost + summary.pmCost)}</td>
            </tr>
            <tr>
              <td>Less: BC Architect Oversight ({bcArchitectPct}%)</td>
              <td className="text-right text-red-600">-{formatCurrency(summary.bcOversightCost)}</td>
            </tr>
            <tr>
              <td>Less: BC Project Management ({bcPmPctVal}%)</td>
              <td className="text-right text-red-600">-{formatCurrency(summary.bcPmCost)}</td>
            </tr>
            <tr>
              <td>Less: Expenses</td>
              <td className="text-right text-red-600">-{formatCurrency(0)}</td>
            </tr>
            <tr className="total-row">
              <td>= Remaining Services</td>
              <td className={`text-right text-lg font-bold ${remainingEntitlement >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {formatCurrency(remainingEntitlement)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Resource Allocation by Role */}
      {Object.keys(summary.byRole).length > 0 && (
        <div className="card overflow-hidden !p-0">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-xtravirt-dark flex items-center gap-2">
              <Users className="h-4 w-4 text-xtravirt-navy" />
              Resource Allocation by Role
            </h4>
          </div>
          <table className="resource-table">
            <thead>
              <tr>
                <th>Role</th>
                <th className="text-right">Days</th>
                <th className="text-right">Hours</th>
                <th className="text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.byRole)
                .sort((a, b) => b[1].cost - a[1].cost)
                .map(([role, data]) => (
                  <tr key={role}>
                    <td>{role}</td>
                    <td className="text-right font-medium">{formatDays(data.days)}</td>
                    <td className="text-right">{data.hours.toFixed(0)}</td>
                    <td className="text-right font-medium text-green-700">{formatCurrency(data.cost)}</td>
                  </tr>
                ))}
              {/* XV oversight rows */}
              <tr className="bg-gray-50">
                <td>XV Architect Oversight</td>
                <td className="text-right font-medium">{formatDays(summary.oversightDays)}</td>
                <td className="text-right">{(summary.oversightDays * 8).toFixed(0)}</td>
                <td className="text-right font-medium text-green-700">{formatCurrency(summary.oversightCost)}</td>
              </tr>
              <tr className="bg-gray-50">
                <td>XV Project Management</td>
                <td className="text-right font-medium">{formatDays(summary.pmDays)}</td>
                <td className="text-right">{(summary.pmDays * 8).toFixed(0)}</td>
                <td className="text-right font-medium text-green-700">{formatCurrency(summary.pmCost)}</td>
              </tr>
              {/* BC oversight rows */}
              <tr className="bg-blue-50">
                <td>BC Architect Oversight</td>
                <td className="text-right font-medium">{formatDays(summary.bcOversightDays)}</td>
                <td className="text-right">{(summary.bcOversightDays * 8).toFixed(0)}</td>
                <td className="text-right font-medium text-green-700">{formatCurrency(summary.bcOversightCost)}</td>
              </tr>
              <tr className="bg-blue-50">
                <td>BC Project Management</td>
                <td className="text-right font-medium">{formatDays(summary.bcPmDays)}</td>
                <td className="text-right">{(summary.bcPmDays * 8).toFixed(0)}</td>
                <td className="text-right font-medium text-green-700">{formatCurrency(summary.bcPmCost)}</td>
              </tr>
              {/* Total row */}
              <tr className="bg-xtravirt-navy/5 font-semibold">
                <td>Total</td>
                <td className="text-right">{formatDays(summary.totalDays)}</td>
                <td className="text-right">{summary.totalHours.toFixed(0)}</td>
                <td className="text-right text-green-700">{formatCurrency(summary.totalProjectCost)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Skills Profile */}
      {totalSkillDays > 0 && Object.keys(summary.bySkill).length > 0 && (
        <div className="card overflow-hidden !p-0">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-xtravirt-dark flex items-center gap-2">
              <Wrench className="h-4 w-4 text-xtravirt-navy" />
              Skills Profile
            </h4>
          </div>
          <table className="resource-table">
            <thead>
              <tr>
                <th>Skill</th>
                <th className="text-right">Days</th>
                <th className="text-right">Hours</th>
                <th className="text-right">Distribution %</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summary.bySkill)
                .filter(([, data]) => data.days > 0)
                .sort((a, b) => b[1].days - a[1].days)
                .map(([skill, data]) => (
                  <tr key={skill}>
                    <td>{skill}</td>
                    <td className="text-right font-medium">{formatDays(data.days)}</td>
                    <td className="text-right">{data.hours.toFixed(0)}</td>
                    <td className="text-right">{((data.days / totalSkillDays) * 100).toFixed(1)}%</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Workstream Breakdown */}
      {Object.keys(summary.byWorkstream).length > 0 && (
        <div className="card overflow-hidden !p-0">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-xtravirt-dark flex items-center gap-2">
              <Layers className="h-4 w-4 text-xtravirt-navy" />
              Workstream Breakdown
            </h4>
          </div>
          <table className="workstream-breakdown-table">
            <thead>
              <tr>
                <th>Workstream</th>
                <th className="text-right">Allocation %</th>
                <th className="text-right">Start Week</th>
                <th className="text-right">End Week</th>
                <th className="text-right">Duration</th>
                <th className="text-right">Days</th>
                <th className="text-right">Hours</th>
                <th className="text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(summary.byWorkstream)
                .sort((a, b) => a.startWeek - b.startWeek)
                .map((ws) => (
                  <tr key={ws.name}>
                    <td className="font-medium">{ws.name}</td>
                    <td className="text-right">{ws.allocationPct.toFixed(1)}%</td>
                    <td className="text-right">W{ws.startWeek}</td>
                    <td className="text-right">W{ws.endWeek}</td>
                    <td className="text-right">{ws.endWeek - ws.startWeek + 1} weeks</td>
                    <td className="text-right font-medium">{formatDays(ws.days)}</td>
                    <td className="text-right">{ws.hours.toFixed(0)}</td>
                    <td className="text-right font-medium text-green-700">{formatCurrency(ws.cost)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Workstream Allocation by Resource */}
      {Object.keys(summary.byWorkstream).length > 0 && summary.allRoles.length > 0 && (
        <div className="card overflow-hidden !p-0">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-xtravirt-dark flex items-center gap-2">
              <Grid3X3 className="h-4 w-4 text-xtravirt-navy" />
              Workstream Allocation by Resource
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="resource-table">
              <thead>
                <tr>
                  <th>Workstream</th>
                  {summary.allRoles.map((role) => (
                    <th key={role} className="text-right">{role}</th>
                  ))}
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(summary.byWorkstream)
                  .sort((a, b) => a.startWeek - b.startWeek)
                  .map((ws) => (
                    <tr key={ws.name}>
                      <td className="font-medium">{ws.name}</td>
                      {summary.allRoles.map((role) => (
                        <td key={role} className="text-right">
                          {ws.byRole[role] ? formatDays(ws.byRole[role]) : '-'}
                        </td>
                      ))}
                      <td className="text-right font-medium">{formatDays(ws.days)}</td>
                    </tr>
                  ))}
                {/* Totals row */}
                <tr className="bg-xtravirt-navy/5 font-semibold">
                  <td>Total</td>
                  {summary.allRoles.map((role) => {
                    const total = Object.values(summary.byWorkstream).reduce(
                      (sum, ws) => sum + (ws.byRole[role] || 0),
                      0
                    );
                    return (
                      <td key={role} className="text-right">
                        {total > 0 ? formatDays(total) : '-'}
                      </td>
                    );
                  })}
                  <td className="text-right">
                    {formatDays(
                      Object.values(summary.byWorkstream).reduce((sum, ws) => sum + ws.days, 0)
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedCapabilities.size === 0 && (
        <div className="text-center py-12 text-gray-500">
          <PieChart className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No capabilities selected</h3>
          <p className="text-sm">Select capabilities from the Capabilities tab to see the summary.</p>
        </div>
      )}
    </div>
  );
}
